// GISAlertsEmbed.tsx
import { useEffect, useState } from "react";
import "./GISAlertsEmbed.css";

// Icons
import { AiFillFire } from "react-icons/ai";
import { BsFillLightningChargeFill } from "react-icons/bs";

interface AlertRow {
    gis_alert_id: number;
    fire_id: string | null;
    fire_name: string;
    fire_code: string;
    fire_url: string | null;
    distance: string;
    line_id: string;
    asset_id: string | null;
    asset_url: string | null;
    active: boolean;
    strike_time: string;
    fire_start_estimated: string | null;
}

const formatDateTime = (value: string | null) => {
    if (!value) return "Unknown";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

export default function GISAlertsEmbed() {
    const [alerts, setAlerts] = useState<AlertRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = () => {
            fetch("/api/esri_alerts_feed/")
                .then((res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.json();
                })
                .then((data: AlertRow[]) => {
                    setAlerts(data);
                    setError(null);
                })
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
        };
        load();
        const interval = setInterval(load, 60000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="fnn-alert-box-wrapper">
            <div className="fnn-alert-box">
                <h3 className="fnn-alert-title">Incident Alerts</h3>
                <div className="fnn-alert-list">
                    {error && <p className="fnn-alert-status fnn-alert-status--error">Could not load alerts ({error}).</p>}
                    {!error && loading && <p className="fnn-alert-status">Loading alerts…</p>}
                    {!error && !loading && alerts.length === 0 && (
                        <p className="fnn-alert-status">No active alerts.</p>
                    )}
                    {alerts.map((a) => (
                        <div className="fnn-alert-item" key={a.gis_alert_id}>
                            <span className="fnn-alert-icon" aria-hidden="true">⚠</span>
                            <div className="fnn-alert-content">
                                <p className="fnn-alert-text">
                                    {a.fire_url ? (
                                        <a href={a.fire_url} target="_blank" rel="noopener noreferrer">
                                            {a.fire_name}
                                        </a>
                                    ) : (
                                        <strong>{a.fire_name}</strong>
                                    )}{" "}
                                    incident detected {a.distance} away from Distribution Line{" "}
                                    {a.asset_url ? (
                                        <a href={a.asset_url} target="_blank" rel="noopener noreferrer">
                                            {a.line_id}
                                        </a>
                                    ) : (
                                        a.line_id
                                    )}
                                    .{" "}
                                    {a.fire_url && (
                                        <>
                                            Please click{" "}
                                            <a href={a.fire_url} target="_blank" rel="noopener noreferrer">
                                                here
                                            </a>{" "}
                                            to discover more information on the fire.
                                        </>
                                    )}
                                </p>

                                <div className="fnn-alert-meta">
                                    <span className="fnn-alert-meta-item">
                                        <BsFillLightningChargeFill className="fnn-alert-meta-icon-lightning" />
                                        <strong>Strike Time:</strong>
                                        {formatDateTime(a.strike_time)}
                                    </span>

                                    {a.fire_start_estimated && (
                                        <span className="fnn-alert-meta-item">
                                            <AiFillFire className="fnn-alert-meta-icon-fire" />
                                            <strong>Estimated Fire Start:</strong>
                                            {formatDateTime(a.fire_start_estimated)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}